import { CUSTOM_EASE, isCubicEase } from './ease';
import { EASES, PROPERTIES, getProperty } from './properties';
import { isBuiltinProperty, registryProperty, resolveProperty } from './registry';

let sequence = 0;

export const createId = (prefix) => {
    sequence += 1;
    return `${prefix}-${sequence.toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
};

export const roundTime = (time) => Math.round(time * 10000) / 10000;

const clampNumber = (value, min, max, fallback) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
};

export const assetDataUrl = (asset) => {
    if (!asset?.base64) return null;
    return `data:${asset.mimeType || 'image/png'};base64,${asset.base64}`;
};

const PROPERTY_ID = /^[A-Za-z_][A-Za-z0-9_]*$/;

const normalizeCustomProperties = (properties) => {
    const seen = new Set();
    return (Array.isArray(properties) ? properties : []).map((property) => {
        const id = String(property?.id || '').trim();
        if (!PROPERTY_ID.test(id) || isBuiltinProperty(id) || seen.has(id)) return null;
        seen.add(id);
        const step = Number(property.step);
        return {
            id,
            label: String(property.label || id).trim() || id,
            defaultValue: Number.isFinite(Number(property.defaultValue)) ? Number(property.defaultValue) : 0,
            step: Number.isFinite(step) && step > 0 ? step : 0.01,
        };
    }).filter(Boolean);
};

const normalizeAsset = (asset) => {
    if (!asset || typeof asset.base64 !== 'string' || asset.base64.length === 0) return null;
    return {
        fileName: String(asset.fileName || 'image.png'),
        mimeType: String(asset.mimeType || 'image/png'),
        base64: asset.base64,
    };
};

const propertyById = (id, properties) => (
    getProperty(id) || properties.find((property) => property.id === id) || null
);

export const storedValue = (value, property) => {
    if (property?.type === 'boolean') {
        if (value === true || value === false) return value;
        return null;
    }
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
};

const normalizeInitial = (initial, properties) => {
    if (!initial || typeof initial !== 'object') return undefined;
    const known = new Set([
        ...PROPERTIES.map((property) => property.id),
        ...properties.map((property) => property.id),
    ]);
    const result = {};
    Object.entries(initial).forEach(([id, value]) => {
        if (!known.has(id)) return;
        const stored = storedValue(value, propertyById(id, properties));
        if (stored == null) return;
        result[id] = stored;
    });
    return Object.keys(result).length ? result : undefined;
};

const normalizeKey = (key, property) => {
    const value = storedValue(key?.value, property);
    if (!key || !Number.isFinite(Number(key.time)) || value == null) return null;
    const ease = typeof key.ease === 'string' && key.ease.trim() ? key.ease : 'none';
    return {
        id: String(key.id || createId('key')),
        time: roundTime(Math.max(0, Number(key.time))),
        value,
        ease,
    };
};

export const normalizeClip = (input) => {
    const source = input && typeof input === 'object' ? input : {};
    const actors = (Array.isArray(source.actors) ? source.actors : []).map((actor, index) => {
        if (!actor || typeof actor !== 'object') return null;
        const properties = normalizeCustomProperties(actor.properties);
        const initial = normalizeInitial(actor.initial, properties);
        return {
            id: String(actor.id || createId('actor')),
            name: String(actor.name || `Actor ${index + 1}`),
            asset: normalizeAsset(actor.asset),
            properties,
            ...(initial ? { initial } : {}),
        };
    }).filter(Boolean);

    const seen = new Set();
    const uniqueActors = actors.filter((actor) => {
        if (seen.has(actor.id)) return false;
        seen.add(actor.id);
        return true;
    });

    const length = clampNumber(source.length, 0.05, 120, 1);
    const tracks = (Array.isArray(source.tracks) ? source.tracks : []).map((track) => {
        const actor = uniqueActors.find((item) => item.id === String(track?.actorId));
        const propertyId = String(track?.property || '');
        const known = getProperty(propertyId)
            || actor?.properties?.some((property) => property.id === propertyId)
            || registryProperty(actor?.id, propertyId);
        if (!track || !known || !actor) return null;
        const property = propertyById(propertyId, actor.properties || []);
        const keys = (Array.isArray(track.keys) ? track.keys : [])
            .map((key) => normalizeKey(key, property))
            .filter(Boolean)
            .map((key) => ({ ...key, time: Math.min(length, key.time) }))
            .sort((a, b) => a.time - b.time);
        return {
            id: String(track.id || createId('track')),
            actorId: String(track.actorId),
            property: track.property,
            keys,
        };
    }).filter(Boolean);

    return {
        version: 1,
        name: String(source.name || 'Untitled'),
        length,
        frameRate: clampNumber(source.frameRate, 1, 120, 60),
        loop: source.loop === true,
        keepFinal: source.keepFinal === true,
        actors: uniqueActors,
        tracks,
    };
};

export const createEmptyClip = (name = 'New Animation') => normalizeClip({
    name,
    length: 1,
    frameRate: 60,
    actors: [],
    tracks: [],
});

export const updateActor = (clip, actorId, patch) => normalizeClip({
    ...clip,
    actors: clip.actors.map((actor) => (actor.id === actorId ? { ...actor, ...patch, id: actor.id } : actor)),
});

export const addAnimatable = (clip, animatable) => {
    if (!animatable?.id) return { clip, actorId: null };
    if (clip.actors.some((actor) => actor.id === animatable.id)) {
        return { clip, actorId: animatable.id };
    }

    const actor = { id: animatable.id, name: animatable.name || animatable.id, asset: null, properties: [] };
    return {
        clip: normalizeClip({ ...clip, actors: [...clip.actors, actor] }),
        actorId: actor.id,
    };
};

export const setActorInitial = (clip, actorId, propertyId, value) => {
    const actor = clip.actors.find((item) => item.id === actorId);
    const property = resolveProperty(actorId, propertyId, actor?.properties);
    const stored = storedValue(value, property);
    if (!actor || stored == null) return clip;
    return updateActor(clip, actorId, { initial: { ...(actor.initial || {}), [propertyId]: stored } });
};

export const clearActorInitial = (clip, actorId, propertyId) => {
    const actor = clip.actors.find((item) => item.id === actorId);
    if (!actor?.initial || !(propertyId in actor.initial)) return clip;
    const initial = { ...actor.initial };
    delete initial[propertyId];
    return updateActor(clip, actorId, { initial });
};

export const addCustomProperty = (clip, actorId, property) => {
    const id = String(property?.id || '').trim();
    const actor = clip.actors.find((item) => item.id === actorId);
    if (!actor || !PROPERTY_ID.test(id) || isBuiltinProperty(id)) return clip;

    const next = {
        id,
        label: String(property.label || id).trim() || id,
        defaultValue: Number.isFinite(Number(property.defaultValue)) ? Number(property.defaultValue) : 0,
        step: Number(property.step) > 0 ? Number(property.step) : 0.01,
    };
    const properties = [...(actor.properties || []).filter((item) => item.id !== id), next];
    return updateActor(clip, actorId, { properties });
};

export const removeCustomProperty = (clip, actorId, propertyId) => normalizeClip({
    ...clip,
    actors: clip.actors.map((actor) => (
        actor.id === actorId
            ? { ...actor, properties: (actor.properties || []).filter((property) => property.id !== propertyId) }
            : actor
    )),
    tracks: clip.tracks.filter((track) => !(track.actorId === actorId && track.property === propertyId)),
});

export const removeActor = (clip, actorId) => normalizeClip({
    ...clip,
    actors: clip.actors.filter((actor) => actor.id !== actorId),
    tracks: clip.tracks.filter((track) => track.actorId !== actorId),
});

export const addTrack = (clip, actorId, property) => {
    const actor = clip.actors.find((item) => item.id === actorId);
    const prop = resolveProperty(actorId, property, actor?.properties);
    if (!prop || clip.tracks.some((track) => track.actorId === actorId && track.property === property)) {
        return { clip, trackId: null };
    }

    const initial = storedValue(actor.initial?.[property], prop);
    const track = {
        id: createId('track'),
        actorId,
        property,
        keys: [
            {
                id: createId('key'),
                time: 0,
                value: initial == null ? prop.defaultValue : initial,
                ease: 'none',
            },
        ],
    };

    return {
        clip: normalizeClip({ ...clip, tracks: [...clip.tracks, track] }),
        trackId: track.id,
    };
};

export const removeTrack = (clip, trackId) => normalizeClip({
    ...clip,
    tracks: clip.tracks.filter((track) => track.id !== trackId),
});

export const addKey = (clip, trackId, time, value) => {
    const track = clip.tracks.find((item) => item.id === trackId);
    const actor = clip.actors.find((item) => item.id === track?.actorId);
    const property = resolveProperty(track?.actorId, track?.property, actor?.properties);
    if (!track || !property) return clip;

    const nextTime = roundTime(Math.min(clip.length, Math.max(0, time)));
    const stored = storedValue(value, property);
    const keys = track.keys.filter((key) => key.time !== nextTime);
    keys.push({
        id: createId('key'),
        time: nextTime,
        value: stored == null ? property.defaultValue : stored,
        ease: property.type === 'boolean' ? 'none' : 'power2.inOut',
    });
    keys.sort((a, b) => a.time - b.time);

    return normalizeClip({
        ...clip,
        tracks: clip.tracks.map((item) => (item.id === trackId ? { ...item, keys } : item)),
    });
};

export const updateKey = (clip, trackId, keyId, patch) => normalizeClip({
    ...clip,
    tracks: clip.tracks.map((track) => {
        if (track.id !== trackId) return track;
        const keys = track.keys.map((key) => (key.id === keyId ? { ...key, ...patch } : key));
        keys.sort((a, b) => a.time - b.time);
        return { ...track, keys };
    }),
});

export const removeKey = (clip, trackId, keyId) => normalizeClip({
    ...clip,
    tracks: clip.tracks.map((track) => (
        track.id === trackId
            ? { ...track, keys: track.keys.filter((key) => key.id !== keyId) }
            : track
    )),
});

export const setClipLength = (clip, length) => normalizeClip({ ...clip, length });

export const setClipFrameRate = (clip, frameRate) => normalizeClip({ ...clip, frameRate });

export const snapTime = (time, frameRate, length, enabled) => {
    const clamped = Math.min(length, Math.max(0, time));
    if (!enabled) return roundTime(clamped);
    const frames = Math.round(clamped * frameRate);
    return roundTime(Math.min(length, frames / frameRate));
};

export const easeOptions = (current) => {
    const named = current && current !== CUSTOM_EASE && !isCubicEase(current) && !EASES.includes(current)
        ? [current, ...EASES]
        : EASES;
    return [...named, CUSTOM_EASE];
};

const clipFileName = (clip) => {
    const slug = clip.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'animation';
    return `${slug}.json`;
};

const downloadClip = (fileName, contents) => {
    const blob = new Blob([contents], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
};

export const saveClip = async (clip, fileName = clipFileName(normalizeClip(clip))) => {
    const normalized = normalizeClip(clip);
    const contents = JSON.stringify(normalized, null, 2);

    if (typeof window.showSaveFilePicker !== 'function') {
        downloadClip(fileName, contents);
        return fileName;
    }

    const handle = await window.showSaveFilePicker({
        suggestedName: fileName,
        types: [{
            description: 'Animation clip',
            accept: { 'application/json': ['.json'] },
        }],
    });
    const writable = await handle.createWritable();
    await writable.write(contents);
    await writable.close();
    return handle.name;
};

export const fileToAsset = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
        const dataUrl = String(reader.result || '');
        const splitAt = dataUrl.indexOf(',');
        const header = dataUrl.slice(0, splitAt);
        const mimeType = header.match(/data:(.*?);base64/)?.[1] || file.type || 'image/png';
        resolve({
            fileName: file.name,
            mimeType,
            base64: dataUrl.slice(splitAt + 1),
        });
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});
